<script setup lang="ts">
import { NxtModal, NxtTabs } from '@future-harry/nxt-styles';
import { createDefaultJobBoard } from '@/core/collections/job-boards/job-board.helper';
import { SettingsTabId } from '@/apps/admin/components/settings/constants';
import { JobBoard } from '@/core/collections/job-boards/job-board.interface';
import { useJobBoardSettings } from '@/apps/admin/composables/job-board-settings.composable';
import { useSettingsStore } from '@/apps/admin/stores/settings.store';
import GeneralSettings from '@/apps/admin/components/settings/tabs/GeneralSettings.vue';
import FooterSettings from '../settings/tabs/FooterSettings.vue';
import HeaderSettings from '../settings/tabs/HeaderSettings.vue';
import ThemeSettings from '../settings/tabs/ThemeSettings.vue';

const emits = defineEmits<{
   (e: 'onClose'): void;
   (e: 'onCreate', jobBoard: JobBoard): void;
}>();

const jobBoard: Ref<JobBoard> = ref(createDefaultJobBoard());
const { validate } = useJobBoardSettings(jobBoard);
const settingsStore = useSettingsStore();

// always start with a fresh job board
onMounted(() => {
   jobBoard.value = createDefaultJobBoard();
   settingsStore.setActiveTab(SettingsTabId.General);
});

const createJobBoard = () => {
   if (!validate()) {
      return;
   }
   emits('onCreate', jobBoard.value);
};
</script>

<template>
   <NxtModal
      :title="$t('actions.add-type', { type: $t('jobsites.jobsites') })"
      :primary-action-text="$t('actions.add-type', { type: $t('jobsites.jobsites') })"
      :cancel-text="$t('actions.cancel')"
      @on-primary-action="createJobBoard"
      @on-cancel="$emit('onClose')"
      width="84.6rem"
      height="84.6rem"
   >
      <NxtTabs class="settings-tabs">
         <a
            :class="['settings-tabs__tab', { active: settingsStore.activeTab === SettingsTabId.General }]"
            @click="settingsStore.setActiveTab(SettingsTabId.General)"
            >{{ $t('job-board.settings.general') }}</a
         >
         <a
            :class="['settings-tabs__tab', { active: settingsStore.activeTab === SettingsTabId.Header }]"
            @click="settingsStore.setActiveTab(SettingsTabId.Header)"
            >{{ $t('job-board.settings.header') }}</a
         >
         <a
            :class="['settings-tabs__tab', { active: settingsStore.activeTab === SettingsTabId.Footer }]"
            @click="settingsStore.setActiveTab(SettingsTabId.Footer)"
            >{{ $t('job-board.settings.footer') }}</a
         >
         <a
            :class="['settings-tabs__tab', { active: settingsStore.activeTab === SettingsTabId.Theme }]"
            @click="settingsStore.setActiveTab(SettingsTabId.Theme)"
            >{{ $t('job-board.settings.theme.theme') }}</a
         >
      </NxtTabs>
      <div>
         <GeneralSettings
            v-if="settingsStore.activeTab === SettingsTabId.General"
            v-model="jobBoard"
            :isMediaSectionEnabled="false"
            class="settings-form-layout"
         />
         <HeaderSettings v-if="settingsStore.activeTab === SettingsTabId.Header" v-model="jobBoard" class="settings-form-layout" />
         <FooterSettings v-if="settingsStore.activeTab === SettingsTabId.Footer" v-model="jobBoard" class="settings-form-layout" />
         <ThemeSettings v-if="settingsStore.activeTab === SettingsTabId.Theme" v-model="jobBoard" class="settings-form-layout" />
      </div>
   </NxtModal>
</template>

<style scoped lang="scss">
:deep(.settings-tabs) {
   margin-bottom: var(--nxt-gutter-large);

   .settings-tabs__tab {
      font-size: var(--nxt-font-xlarge);
      font-weight: var(--nxt-font-bold);

      &.active {
         color: var(--nxt-dark) !important;
      }
   }
}
</style>
